import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Building2,
  Users,
  Bell,
  Calendar,
  MapPin,
  CheckCircle2,
  Bike,
  UserCheck,
  Award,
  TrendingUp,
  TrendingDown,
  Activity,
  Sparkles,
  Clock,
  IndianRupee,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listTemples } from "@/lib/temples-api";
import { listVolunteers } from "@/lib/volunteers-api";
import { listRickshaws } from "@/lib/rickshaws-api";
import { listMembers } from "@/lib/members-api";
import { listAnnouncements } from "@/lib/announcements-api";
import { listBookings } from "@/lib/bookings-api";
import { listIssuedCertificates } from "@/lib/certificates-api";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

export const Route = createFileRoute("/admin/")({ component: Dashboard });

const PALETTE = [
  "#EB5206",
  "#D44804",
  "#B83D04",
  "#8F2E02",
  "#FF9E7A",
  "#FB923C",
  "#FDBA74",
  "#FED7AA",
];

function Dashboard() {
  // Counts fetched from the live list endpoints (shared cache with each admin page).
  const temples = useQuery({ queryKey: ["jgy-temples"], queryFn: listTemples });
  const members = useQuery({ queryKey: ["jgy-members"], queryFn: listMembers });
  const volunteers = useQuery({ queryKey: ["jgy-volunteers"], queryFn: listVolunteers });
  const rickshaws = useQuery({ queryKey: ["jgy-rickshaws"], queryFn: listRickshaws });
  const bookings = useQuery({ queryKey: ["jgy-bookings"], queryFn: listBookings });
  const announcements = useQuery({ queryKey: ["jgy-announcements"], queryFn: listAnnouncements });
  const issuedCerts = useQuery({ queryKey: ["jgy-cert-issued"], queryFn: listIssuedCertificates });

  const counts = {
    temples: temples.data?.length ?? 0,
    members: members.data?.length ?? 0,
    volunteers: volunteers.data?.length ?? 0,
    rickshaws: rickshaws.data?.length ?? 0,
    bookings: bookings.data?.length ?? 0,
    ann: (announcements.data ?? []).filter((a) => a.active).length,
    certs: issuedCerts.data?.length ?? 0,
    visits: 0,
  };

  const [now, setNow] = useState<string>("");
  useEffect(() => {
    const tick = () => {
      const d = new Date();
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      setNow(`${dd}-${mm}-${d.getFullYear()} ${hh}:${mi}`);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, []);
  // Featured temples — first 6 by yatra order.
  const recent = useMemo(
    () => [...(temples.data ?? [])].sort((a, b) => a.yatra_order - b.yatra_order).slice(0, 6),
    [temples.data],
  );

  // Recent announcements (mapped to the card's expected fields).
  const ann = useMemo(
    () =>
      (announcements.data ?? [])
        .slice(0, 4)
        .map((a) => ({ id: a.id, title_en: a.title, body_en: a.description, active: a.active })),
    [announcements.data],
  );

  // Temples grouped by area.
  const areaData = useMemo(() => {
    const m = new Map<string, number>();
    (temples.data ?? []).forEach((t) => {
      if (t.area) {
        m.set(t.area, (m.get(t.area) || 0) + 1);
      }
    });
    return Array.from(m.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [temples.data]);

  // Bookings grouped by status / type.
  const bookingStatus = useMemo(() => {
    const m = new Map<string, number>();
    (bookings.data ?? []).forEach((b) => {
      const k = b.status || "unknown";
      m.set(k, (m.get(k) || 0) + 1);
    });
    return Array.from(m.entries()).map(([name, value]) => ({ name, value }));
  }, [bookings.data]);
  const bookingType = useMemo(() => {
    const m = new Map<string, number>();
    (bookings.data ?? []).forEach((b) => {
      const k = b.type || "unknown";
      m.set(k, (m.get(k) || 0) + 1);
    });
    return Array.from(m.entries()).map(([name, value]) => ({ name, value }));
  }, [bookings.data]);

  // Top volunteers by experience.
  const topVolunteers = useMemo(
    () =>
      [...(volunteers.data ?? [])]
        .sort((a, b) => b.experience_years - a.experience_years)
        .slice(0, 6),
    [volunteers.data],
  );

  // 14-day activity trend derived from real record dates (no dedicated time-series API).
  const trend = useMemo(() => {
    const pad = (n: number) => String(n).padStart(2, "0");
    const today = new Date();
    const days = Array.from({ length: 14 }, (_, idx) => {
      const d = new Date(today);
      d.setDate(today.getDate() - (13 - idx));
      return {
        day: `${d.getDate()}/${d.getMonth() + 1}`,
        key: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
        bookings: 0,
        visits: 0,
        signups: 0,
      };
    });
    const byKey = new Map(days.map((x) => [x.key, x]));

    (bookings.data ?? []).forEach((b) => {
      const k = (b.booking_date || b.created_at || "").slice(0, 10);
      const row = byKey.get(k);
      if (row) {
        row.bookings += 1;
        if (b.type === "visit") {
          row.visits += 1;
        }
      }
    });
    (members.data ?? []).forEach((m) => {
      const k = String((m as { created_at?: string }).created_at ?? "").slice(0, 10);
      const row = byKey.get(k);
      if (row) {
        row.signups += 1;
      }
    });
    return days;
  }, [bookings.data, members.data]);

  // Average yatra completion across members (visited / total).
  const completionPct = useMemo(() => {
    const ms = (members.data ?? []).filter((m) => m.total > 0);
    if (!ms.length) {
      return 0;
    }
    const avg = ms.reduce((s, m) => s + m.visited / m.total, 0) / ms.length;
    return Math.min(100, Math.round(avg * 100));
  }, [members.data]);
  const radialData = [{ name: "Yatra", value: completionPct, fill: "#EB5206" }];

  const stats = [
    {
      label: "Temples",
      value: counts.temples,
      icon: Building2,
      hint: "Derasars mapped",
      trend: "—",
      up: true,
      to: "/admin/temples" as const,
    },
    {
      label: "Members",
      value: counts.members,
      icon: Users,
      hint: "Registered yatris",
      trend: "—",
      up: true,
      to: "/admin/members" as const,
    },
    {
      label: "Volunteers",
      value: counts.volunteers,
      icon: UserCheck,
      hint: "Available seva",
      trend: "—",
      up: true,
      to: "/admin/volunteers" as const,
    },
    {
      label: "Rickshaws",
      value: counts.rickshaws,
      icon: Bike,
      hint: "Active drivers",
      trend: "—",
      up: true,
      to: "/admin/rickshaws" as const,
    },
    {
      label: "Bookings",
      value: counts.bookings,
      icon: Calendar,
      hint: "All time",
      trend: "—",
      up: true,
      to: "/admin/bookings" as const,
    },
    {
      label: "Certificates",
      value: counts.certs,
      icon: Award,
      hint: "Issued",
      trend: "—",
      up: true,
      to: "/admin/certificates" as const,
    },
    {
      label: "Announcements",
      value: counts.ann,
      icon: Bell,
      hint: "Live",
      trend: "—",
      up: true,
      to: "/admin/announcements" as const,
    },
    {
      label: "Temple Check-ins",
      value: counts.visits,
      icon: CheckCircle2,
      hint: "QR scans",
      trend: "—",
      up: counts.visits > 0,
      to: "/admin/temples" as const,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Hero strip */}
      <div
        className="relative overflow-hidden rounded-3xl border border-border p-6 text-primary-foreground"
        style={{ background: "var(--gradient-saffron)" }}
      >
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-12 -left-6 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-90">
              <Sparkles className="h-3.5 w-3.5" /> Live Yatra Insights
            </div>
            <h1 className="mt-1 font-serif text-3xl font-bold sm:text-4xl">
              Jain Gaurav Yatra · Command Center
            </h1>
            <p className="mt-1 text-sm opacity-90">
              Real-time view across temples, devotees, volunteers, bookings & seva.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-white/15 px-4 py-2 text-sm backdrop-blur">
              <Clock className="mr-1 inline h-3.5 w-3.5" /> {now || "—"}
            </div>
            <div className="rounded-2xl bg-white/15 px-4 py-2 text-sm backdrop-blur">
              <Activity className="mr-1 inline h-3.5 w-3.5" /> System Healthy
            </div>
          </div>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {stats.map((s) => (
          <Link
            key={s.label}
            to={s.to}
            className="group relative overflow-hidden rounded-2xl border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[var(--shadow-warm)]"
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {s.label}
              </span>
              <span className="icon-tile h-8 w-8">
                <s.icon className="h-4 w-4" />
              </span>
            </div>
            <div className="mt-2 font-serif text-2xl font-bold leading-none">{s.value}</div>
            <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
              <span>{s.hint}</span>
              <span
                className={`flex items-center gap-0.5 font-bold ${s.up ? "text-green-600" : "text-destructive"}`}
              >
                {s.up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{" "}
                {s.trend}
              </span>
            </div>
            <div
              className="absolute inset-x-0 bottom-0 h-0.5 origin-left scale-x-0 transition-transform group-hover:scale-x-100"
              style={{ background: "var(--gradient-saffron)" }}
            />
          </Link>
        ))}
      </div>

      {/* Trend + Radial */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="font-serif text-lg font-bold">Activity Trend · last 14 days</h3>
              <p className="text-xs text-muted-foreground">
                Bookings, temple check-ins and member sign-ups
              </p>
            </div>
            <div className="flex gap-1.5 text-[10px]">
              <span className="rounded-full bg-primary/15 px-2 py-0.5 font-semibold text-primary">
                Bookings
              </span>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 font-semibold text-primary">
                Visits
              </span>
              <span className="rounded-full bg-maroon/10 px-2 py-0.5 font-semibold text-maroon">
                Signups
              </span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={trend} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#EB5206" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#EB5206" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C94005" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#C94005" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="g3" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF9E7A" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#FF9E7A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#f4e9da" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="#a78169" />
              <YAxis tick={{ fontSize: 10 }} stroke="#a78169" />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: "1px solid #f0d9b9", fontSize: 12 }}
              />
              <Area
                type="monotone"
                dataKey="visits"
                stroke="#C94005"
                strokeWidth={2}
                fill="url(#g2)"
              />
              <Area
                type="monotone"
                dataKey="bookings"
                stroke="#EB5206"
                strokeWidth={2}
                fill="url(#g1)"
              />
              <Area
                type="monotone"
                dataKey="signups"
                stroke="#FF9E7A"
                strokeWidth={2}
                fill="url(#g3)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-serif text-lg font-bold">Yatra Completion</h3>
          <p className="text-xs text-muted-foreground">Average across all members</p>
          <ResponsiveContainer width="100%" height={220}>
            <RadialBarChart
              innerRadius="65%"
              outerRadius="100%"
              data={radialData}
              startAngle={90}
              endAngle={-270}
            >
              <RadialBar background={{ fill: "#fdf2e3" }} dataKey="value" cornerRadius={20} />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="-mt-[140px] text-center">
            <div className="font-serif text-4xl font-bold text-primary">{radialData[0].value}%</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Avg progress
            </div>
          </div>
          <div className="mt-[110px] grid grid-cols-2 gap-2 text-center text-xs">
            <div className="rounded-xl bg-muted p-2">
              <div className="font-bold">{counts.members}</div>
              <div className="text-[10px] text-muted-foreground">Yatris</div>
            </div>
            <div className="rounded-xl bg-muted p-2">
              <div className="font-bold">{counts.certs}</div>
              <div className="text-[10px] text-muted-foreground">Certificates</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bookings analytics */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-serif text-lg font-bold">Booking Status</h3>
          <p className="text-xs text-muted-foreground">Live distribution</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={bookingStatus}
                dataKey="value"
                nameKey="name"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={3}
              >
                {bookingStatus.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-serif text-lg font-bold">Booking Type</h3>
          <p className="text-xs text-muted-foreground">Volunteer vs Rickshaw</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={bookingType} margin={{ top: 10, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid stroke="#f4e9da" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#a78169" />
              <YAxis tick={{ fontSize: 11 }} stroke="#a78169" />
              <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {bookingType.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-serif text-lg font-bold">Member Sign-ups</h3>
          <p className="text-xs text-muted-foreground">14-day pace</p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend} margin={{ top: 10, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid stroke="#f4e9da" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="#a78169" />
              <YAxis tick={{ fontSize: 10 }} stroke="#a78169" />
              <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="signups"
                stroke="#EB5206"
                strokeWidth={3}
                dot={{ r: 3, fill: "#EB5206" }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Areas + Top Volunteers */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="font-serif text-lg font-bold">Temples by Area</h3>
              <p className="text-xs text-muted-foreground">Top localities of Ahmedabad</p>
            </div>
            <Link
              to="/admin/temples"
              className="text-xs font-semibold text-primary hover:underline"
            >
              Manage all →
            </Link>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={areaData}
              layout="vertical"
              margin={{ top: 5, right: 16, left: 8, bottom: 0 }}
            >
              <CartesianGrid stroke="#f4e9da" strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} stroke="#a78169" />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 11 }}
                stroke="#a78169"
                width={90}
              />
              <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                {areaData.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-serif text-lg font-bold">Top Volunteers</h3>
            <Link
              to="/admin/volunteers"
              className="text-xs font-semibold text-primary hover:underline"
            >
              All →
            </Link>
          </div>
          <div className="space-y-2">
            {topVolunteers.map((v, i) => (
              <div
                key={v.name + i}
                className="flex items-center gap-3 rounded-xl border border-border p-2.5"
              >
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-primary-foreground"
                  style={{ background: "var(--gradient-saffron)" }}
                >
                  {(v.name || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm font-semibold">{v.name}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {v.experience_years} yrs experience
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center text-xs font-bold text-primary">
                    <IndianRupee className="h-3 w-3" />
                    {v.charges_per_day}
                  </div>
                  <div className="text-[9px] text-muted-foreground">/day</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Yatra order list + Live announcements */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5 lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-serif text-lg font-bold">Yatra Route — Featured Temples</h3>
            <Link
              to="/admin/temples"
              className="text-xs font-semibold text-primary hover:underline"
            >
              Manage all →
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {recent.map((t) => (
              <Link
                key={t.id}
                to="/admin/temples"
                className="flex items-center justify-between rounded-xl border border-border p-3 hover:bg-muted"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-lg text-primary-foreground"
                    style={{ background: "var(--gradient-saffron)" }}
                  >
                    {t.yatra_order}
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{t.name_en}</div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" /> {t.area}
                    </div>
                  </div>
                </div>
                <span className="rounded-full bg-muted px-3 py-1 text-[10px] font-medium">
                  {t.mool_nayak}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-serif text-lg font-bold">Live Announcements</h3>
            <Link
              to="/admin/announcements"
              className="text-xs font-semibold text-primary hover:underline"
            >
              Manage →
            </Link>
          </div>
          <div className="space-y-2">
            {ann.map((a) => (
              <Link
                key={a.id}
                to="/admin/announcements"
                className="block rounded-xl border border-border p-3 hover:bg-muted"
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">{a.title_en}</div>
                  <span
                    className={`text-[10px] font-bold uppercase ${a.active ? "text-green-600" : "text-muted-foreground"}`}
                  >
                    {a.active ? "● Live" : "Off"}
                  </span>
                </div>
                <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{a.body_en}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Link
          to="/admin/volunteers"
          className="rounded-2xl border border-border bg-card p-4 text-sm font-semibold hover:bg-muted"
        >
          Manage Volunteers →
        </Link>
        <Link
          to="/admin/rickshaws"
          className="rounded-2xl border border-border bg-card p-4 text-sm font-semibold hover:bg-muted"
        >
          Manage Rickshaws →
        </Link>
        <Link
          to="/admin/certificates"
          className="rounded-2xl border border-border bg-card p-4 text-sm font-semibold hover:bg-muted"
        >
          Issue Certificates →
        </Link>
        <Link
          to="/admin/settings"
          className="rounded-2xl border border-border bg-card p-4 text-sm font-semibold hover:bg-muted"
        >
          Portal Settings →
        </Link>
      </div>
    </div>
  );
}
